import type { IBiomaterial } from '@zero-dash/types';
import { getSpecimenPurposeAbbreviation } from '@zero-dash/types';
import { corePalette } from '@/themes/colours';
import type { IAnalysisSet } from '@/types/Analysis/AnalysisSets.types';
import type { IReport } from '@/types/Reports/Reports.types';
import type {
  BiomarkerSelection,
  DiseaseBurdenFilters,
  IBiomarkerData,
  IDiseaseBurdenData,
  IReportTimelineMarker,
  ITimelinePopupBurdenRow,
  ITimelinePopupContent,
  ITimelinePopupSampleEntry,
  ITimelinePopupSampleGroup,
  ITimelineMarker,
} from '@/types/PatientDashboard/PatientTimeline.types';
import { getAssayLabel } from '@/utils/functions/getAssayLabel';
import { getDiseaseBurdenLines } from './DiseaseBurden.util';
import {
  buildCurationHref,
  getReportLabel,
  getSampleAssayLabel,
  getSampleTrackId,
  getValidDate,
  isDisplayableReportType,
} from './PatientTimeline.util';

const NOT_SET_LABEL = 'Not set';

interface ISampleLineageEntry extends ITimelinePopupSampleEntry {
  parentId: number | null;
}

interface ITimelinePopupBucket extends Omit<ITimelinePopupContent, 'samples' | 'curationHref' | 'reportGroups'> {
  samples: ISampleLineageEntry[];
}

export interface IPopupDiseaseBurdenParams {
  data: IDiseaseBurdenData;
  filters: DiseaseBurdenFilters;
  hiddenAnalytes: Record<string, boolean>;
}

export interface IPopupBiomarkerParams {
  data: IBiomarkerData;
  selection: BiomarkerSelection;
}

interface IBuildTimelinePopupContentParams {
  eventMarkers?: ITimelineMarker[];
  biomaterials?: IBiomaterial[];
  reports?: IReport[];
  analysisSets?: IAnalysisSet[];
  diseaseBurden?: IPopupDiseaseBurdenParams;
  biomarker?: IPopupBiomarkerParams;
  patientId?: string;
}

/**
 * Flattens a date's samples into parent-then-children order. A sample whose parent
 * was collected on a different date is a root here, so it renders un-indented rather
 * than orphaned beneath a parent the popup never shows.
 */
export const orderSamplesByLineage = (
  entries: ISampleLineageEntry[],
): ITimelinePopupSampleEntry[] => {
  const present = new Set(entries.map((entry) => entry.biomaterialId));
  const childrenByParentId = new Map<number, ISampleLineageEntry[]>();
  const roots: ISampleLineageEntry[] = [];

  entries.forEach((entry) => {
    const { parentId } = entry;
    if (parentId == null || !present.has(parentId)) {
      roots.push(entry);
      return;
    }
    childrenByParentId.set(parentId, [...(childrenByParentId.get(parentId) ?? []), entry]);
  });

  const visited = new Set<number>();
  const ordered: ITimelinePopupSampleEntry[] = [];

  // Each entry has a single parentId, so the child links form a forest of in-trees and
  // the walk cannot revisit a node: a sample inside a parentId cycle always has its
  // parent present, so it is never a root and is never reached from one.
  const pushWithDescendants = (entry: ISampleLineageEntry, isChild: boolean): void => {
    visited.add(entry.biomaterialId);
    ordered.push({ ...entry, isChild });
    (childrenByParentId.get(entry.biomaterialId) ?? []).forEach((child) => {
      pushWithDescendants(child, true);
    });
  };

  roots.forEach((root) => pushWithDescendants(root, false));

  // Cycle members leave no root to walk from, so whatever the walk missed is listed flat.
  entries
    .filter((entry) => !visited.has(entry.biomaterialId))
    .forEach((entry) => ordered.push({ ...entry, isChild: false }));

  return ordered;
};

/**
 * Splits the flat lineage order into one group per un-indented sample, so the popup can
 * collapse a parent's children behind a "show more" without losing the order the walk
 * produced. A child whose parent is absent from the date opens a group of its own.
 */
export const groupSamplesByLineage = (
  samples: ITimelinePopupSampleEntry[],
): ITimelinePopupSampleGroup[] => {
  const groups: ITimelinePopupSampleGroup[] = [];

  samples.forEach((sample) => {
    const openGroup = groups[groups.length - 1];
    if (sample.isChild && openGroup) {
      openGroup.children.push(sample);
      return;
    }
    groups.push({ parent: sample, children: [] });
  });

  return groups;
};

export const indexAnalysisSetsByBiomaterialId = (
  analysisSets: IAnalysisSet[],
): Map<number, string> => {
  const analysisSetIdByBiomaterialId = new Map<number, string>();

  analysisSets.forEach((set) => (set.biosamples ?? []).forEach((biosample) => {
    if (!analysisSetIdByBiomaterialId.has(biosample.biomaterialId)) {
      analysisSetIdByBiomaterialId.set(biosample.biomaterialId, set.analysisSetId);
    }
  }));

  return analysisSetIdByBiomaterialId;
};

/**
 * A parent biomaterial frequently has no sequenced biosample of its own, so this walks
 * the popup's sample order until one maps to an analysis set rather than giving up on
 * the first entry.
 */
export const resolveCurationAnalysisSetId = (
  biomaterialIds: number[],
  analysisSetIdByBiomaterialId: Map<number, string>,
): string | null => (
  biomaterialIds
    .map((biomaterialId) => analysisSetIdByBiomaterialId.get(biomaterialId))
    .find((analysisSetId): analysisSetId is string => Boolean(analysisSetId)) ?? null
);

/**
 * The Reports-lane case, shaped exactly like the timeline case so one popup renders both.
 * Keyed on the marker date rather than the marker, so hovering any report dot shows every
 * type approved that day; the timeline sections stay empty.
 */
export const buildReportPopupContentByDate = (
  markers: IReportTimelineMarker[],
  trackIds: string[],
  patientId?: string,
): Map<string, ITimelinePopupContent> => {
  const markersByDate = new Map<string, IReportTimelineMarker[]>();
  markers.forEach((marker) => {
    markersByDate.set(marker.date, [...(markersByDate.get(marker.date) ?? []), marker]);
  });

  return new Map(Array.from(markersByDate, ([date, dateMarkers]) => {
    // The lanes are reorderable, so the popup reads in whatever order the timeline draws.
    const ordered = [...dateMarkers].sort(
      (a, b) => trackIds.indexOf(a.reportTrackId) - trackIds.indexOf(b.reportTrackId),
    );
    const newestVersion = ordered
      .flatMap((marker) => marker.reportVersions)
      .sort((a, b) => b.approvedAt.localeCompare(a.approvedAt) || b.version - a.version)[0];

    return [date, {
      date,
      events: [],
      samples: [],
      reports: [],
      diseaseBurden: [],
      biomarkers: [],
      reportGroups: ordered.map((marker) => ({
        type: marker.reportTrackId,
        colour: marker.colour,
        versions: marker.reportVersions,
      })),
      curationHref: patientId && newestVersion
        ? buildCurationHref(patientId, newestVersion.analysisSetId)
        : null,
    }];
  }));
};

/**
 * The timeline case: everything the timeline knows, grouped by calendar date, so hovering
 * any dot outside the Reports lane shows that whole date at once. Keyed on the same
 * `YYYY-MM-DD` the dots publish as `data-marker-date`, so the hover path is a map lookup.
 */
export const buildTimelinePopupContent = ({
  eventMarkers = [],
  biomaterials = [],
  reports = [],
  analysisSets = [],
  diseaseBurden,
  biomarker,
  patientId,
}: IBuildTimelinePopupContentParams): Map<string, ITimelinePopupContent> => {
  const buckets = new Map<string, ITimelinePopupBucket>();

  const bucketFor = (date: string): ITimelinePopupBucket => {
    const existing = buckets.get(date);
    if (existing) {
      return existing;
    }
    const created: ITimelinePopupBucket = {
      date, events: [], samples: [], reports: [], diseaseBurden: [], biomarkers: [],
    };
    buckets.set(date, created);
    return created;
  };

  eventMarkers.forEach((marker) => {
    bucketFor(marker.date).events.push({
      id: marker.id,
      label: marker.label,
      kind: marker.eventKind ?? 'Other',
      colour: marker.colour,
    });
  });

  biomaterials.forEach((biomaterial) => {
    const collectionDate = getValidDate(biomaterial.collectionDate);
    const sampleTrackId = getSampleTrackId(biomaterial);
    // Germline - donor has no track and is never drawn on the timeline.
    if (!collectionDate || !sampleTrackId) {
      return;
    }

    bucketFor(collectionDate.format('YYYY-MM-DD')).samples.push({
      id: `sample-${biomaterial.id}`,
      biomaterialId: biomaterial.id,
      name: biomaterial.name,
      assay: getSampleAssayLabel(biomaterial) || undefined,
      specimenPurposeAbbreviation: getSpecimenPurposeAbbreviation(biomaterial.specimenPurpose),
      subtype: biomaterial.subtype || NOT_SET_LABEL,
      isLiquidBiopsy: sampleTrackId === 'lb',
      isChild: false,
      parentId: biomaterial.parentId,
    });
  });

  reports.forEach((report) => {
    const approvedDate = getValidDate(report.approvedAt);
    if (!approvedDate || !isDisplayableReportType(report.type)) {
      return;
    }
    bucketFor(approvedDate.format('YYYY-MM-DD')).reports.push({
      id: report.id,
      label: getReportLabel(report.type),
    });
  });

  if (diseaseBurden) {
    const { data, filters, hiddenAnalytes } = diseaseBurden;
    data.analytes
      .filter((analyte) => !hiddenAnalytes[analyte.analyte])
      .forEach((analyte) => {
        const rowsByDate = new Map<string, ITimelinePopupBurdenRow[]>();

        getDiseaseBurdenLines(analyte, filters[analyte.analyte]).forEach((line) => {
          line.points.forEach((point) => {
            const row: ITimelinePopupBurdenRow = {
              key: `${line.assay}-${point.id}`,
              assay: line.assay,
              assayLabel: getAssayLabel(line.assay),
              purity: point.purity,
              lod: point.lod,
              isDetected: point.detected,
            };
            rowsByDate.set(point.date, [...(rowsByDate.get(point.date) ?? []), row]);
          });
        });

        rowsByDate.forEach((rows, date) => {
          bucketFor(date).diseaseBurden.push({
            analyte: analyte.analyte,
            colour: analyte.colour,
            rows,
          });
        });
      });
  }

  if (biomarker) {
    const { data, selection } = biomarker;
    const analyteColours = new Map(
      (diseaseBurden?.data.analytes ?? []).map((analyte) => [analyte.analyte, analyte.colour]),
    );

    data.lines
      .filter((line) => selection[line.variantId])
      .forEach((line) => {
        line.points.forEach((point) => {
          bucketFor(point.date).biomarkers.push({
            key: point.id,
            label: line.label,
            lineColour: line.colour,
            assay: point.assay,
            analyteColour: analyteColours.get(point.analyte) ?? corePalette.grey100,
            isDetected: point.altad > 0,
            vaf: point.vaf,
            refad: point.depth - point.altad,
            altad: point.altad,
            depth: point.depth,
          });
        });
      });
  }

  const analysisSetIdByBiomaterialId = indexAnalysisSetsByBiomaterialId(analysisSets);

  return new Map(Array.from(buckets, ([date, bucket]) => {
    const samples = orderSamplesByLineage(bucket.samples);
    const analysisSetId = resolveCurationAnalysisSetId(
      samples.map((sample) => sample.biomaterialId),
      analysisSetIdByBiomaterialId,
    );

    return [date, {
      ...bucket,
      samples,
      reportGroups: [],
      curationHref: patientId && analysisSetId
        ? buildCurationHref(patientId, analysisSetId)
        : null,
    }];
  }));
};
